"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { FinancialWorkQueueSkeleton } from "@/app/financial-work/FinancialWorkViews";
import {
  FINANCIAL_WORK_DESKTOP_SCROLL_CLASS,
  FINANCIAL_WORK_MOBILE_SCROLL_CLASS,
  useFinancialWorkDesktop,
} from "@/lib/financialWork/layout";

type PreparingValue = {
  setReady: (ready: boolean) => void;
  setOverviewReady: (ready: boolean) => void;
  /** Full-page กำลังโหลด — first entry into a workspace only. */
  visible: boolean;
  /** Workspace already completed first load — tab switch can show in-page preparing. */
  initialDone: boolean;
};

const FinancialWorkPreparingContext = createContext<PreparingValue | null>(
  null,
);

export function FinancialWorkPreparingProvider({
  active,
  workspaceId,
  children,
}: {
  active: boolean;
  /** When this changes, preparing cover resets in the same render (same-component setState). */
  workspaceId?: string | null;
  children: ReactNode;
}) {
  const [shellReady, setShellReadyState] = useState(false);
  const [overviewReady, setOverviewReadyState] = useState(true);
  const [initialDone, setInitialDoneState] = useState(false);
  const seenWorkspaceRef = useRef(workspaceId ?? null);
  const nextWorkspace = workspaceId ?? null;
  // Reset in this provider while rendering — legal. Child shells must not call
  // setReady/setOverviewReady during their own render.
  if (seenWorkspaceRef.current !== nextWorkspace) {
    seenWorkspaceRef.current = nextWorkspace;
    setShellReadyState(false);
    setInitialDoneState(false);
    // Leave overviewReady to shell effects (work vs hire) so a workspace
    // change does not briefly force the cover back over an already-open hire chat.
  }

  const setReady = useCallback((next: boolean) => {
    setShellReadyState((prev) => (prev === next ? prev : next));
  }, []);

  const setOverviewReady = useCallback((next: boolean) => {
    setOverviewReadyState((prev) => (prev === next ? prev : next));
  }, []);

  const fullyReady = shellReady && overviewReady;
  if (fullyReady && !initialDone) {
    setInitialDoneState(true);
  }

  const visible = Boolean(active && !initialDone && !fullyReady);

  const value = useMemo(
    () => ({
      setReady,
      setOverviewReady,
      visible,
      initialDone,
    }),
    [initialDone, setOverviewReady, setReady, visible],
  );

  return (
    <FinancialWorkPreparingContext.Provider value={value}>
      {children}
    </FinancialWorkPreparingContext.Provider>
  );
}

export function useFinancialWorkPreparing() {
  return useContext(FinancialWorkPreparingContext);
}

export function FinancialWorkPreparingCover() {
  const preparing = useFinancialWorkPreparing();
  const isDesktop = useFinancialWorkDesktop();
  if (!preparing?.visible) return null;
  return (
    <div
      className="absolute inset-0 z-[100] flex flex-col overflow-hidden bg-aera-white min-[900px]:rounded-tl-2xl min-[1440px]:rounded-tl-3xl"
      data-testid="billin-preparing-cover"
    >
      <div
        className={
          isDesktop
            ? `${FINANCIAL_WORK_DESKTOP_SCROLL_CLASS} px-4 pt-4 min-[900px]:px-6 min-[900px]:pt-5`
            : FINANCIAL_WORK_MOBILE_SCROLL_CLASS
        }
      >
        <FinancialWorkQueueSkeleton />
      </div>
    </div>
  );
}
